import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WhatsappLogo, Link as LinkIcon, FilePdf } from '@phosphor-icons/react';
import { API, BASE } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { fillTemplate, needsQuote, normalizePhone, waLink } from '../lib/whatsapp';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '');

/**
 * Prepare a WhatsApp message for a client and send it from the user's own WhatsApp.
 * With a quote, the PDF itself goes as an attachment through the device's share menu
 * (no link, so the client never sees our domain); a private link (30 days) stays optional.
 */
export default function WhatsAppSendDialog({ open, onOpenChange, quote, client }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [withLink, setWithLink] = useState(false);
  const [share, setShare] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/whatsapp/templates`).then(r => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => setTemplates([]));
    const known = quote?.client_phone || client?.phone || '';
    setPhone(known);
    if (!known && quote?.client_id) {
      axios.get(`${API}/clients/${quote.client_id}`).then(r => setPhone(r.data?.phone || '')).catch(() => {});
    }
    if (quote?.id) {
      // Ready before the click: the share menu and pop-ups only open straight from a tap
      axios.get(`${API}/quotes/${quote.id}/pdf`, { responseType: 'blob' })
        .then(r => setPdfFile(new File([r.data], `Preventivo_${quote.quote_number || 'cliente'}.pdf`, { type: 'application/pdf' })))
        .catch(() => setPdfFile(null));
    }
  }, [open, quote, client]);

  useEffect(() => {
    if (open && withLink && quote?.id && !share) {
      axios.post(`${API}/quotes/${quote.id}/share-link`).then(r => setShare(r.data)).catch(() => setShare(null));
    }
  }, [open, withLink, quote, share]);

  const usable = useMemo(() => templates.filter(t => quote || !needsQuote(t.body)), [templates, quote]);

  useEffect(() => {
    if (!open || !usable.length || templateId) return;
    const first = usable.find(t => (quote ? t.category === 'quote' : t.category !== 'quote')) || usable[0];
    setTemplateId(first.id);
  }, [open, usable, templateId, quote]);

  useEffect(() => {
    const tmpl = usable.find(t => t.id === templateId);
    if (tmpl) setText(fillTemplate(tmpl.body, { quote, client, companyName: user?.company_name }));
  }, [templateId, usable, quote, client, user]);

  const pdfUrl = share ? `${BASE}${share.page_path || share.path}` : '';
  const finalText = withLink && pdfUrl
    ? `${text}\n\nPuò vedere e scaricare il preventivo qui (link valido fino al ${fmtDate(share.expires_at)}):\n${pdfUrl}`
    : text;
  const number = normalizePhone(phone);

  const canShareFile = !!(pdfFile && typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [pdfFile] }));

  const log = (message, channel) => axios.post(`${API}/whatsapp/log`, {
    to_number: number || phone, message, channel, quote_id: quote?.id || null, client_id: quote?.client_id || client?.id || null,
  }).catch(() => {});

  const openWhatsApp = () => {
    if (!number) { toast.error('Inserisci un numero di telefono valido'); return; }
    if (!text.trim()) { toast.error('Scrivi il messaggio'); return; }
    window.open(waLink(number, finalText), '_blank', 'noopener');
    log(finalText, 'link');
    onOpenChange(false);
  };

  // The PDF as a real attachment: the phone's (or Windows') share menu, WhatsApp, the client
  const sendPdf = async () => {
    if (!text.trim()) { toast.error('Scrivi il messaggio'); return; }
    if (canShareFile) {
      // Some WhatsApp versions drop the text when a file is shared: keep it ready to paste
      if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
      try {
        await navigator.share({ files: [pdfFile], text, title: pdfFile.name });
        log(text, 'pdf');
        onOpenChange(false);
        toast.success('PDF condiviso. Se il testo non compare in WhatsApp, incollalo: è già copiato.');
      } catch (err) {
        if (err?.name !== 'AbortError') toast.error('Condivisione non riuscita: riprova o usa "Solo messaggio"');
      }
      return;
    }
    // WhatsApp Web in a browser cannot receive files from a page: download it and open the chat
    if (!number) { toast.error('Inserisci un numero di telefono valido'); return; }
    if (!pdfFile) { toast.error('PDF non ancora pronto, riprova tra un attimo'); return; }
    const url = URL.createObjectURL(pdfFile);
    const a = document.createElement('a');
    a.href = url; a.download = pdfFile.name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    window.open(waLink(number, text), '_blank', 'noopener');
    log(text, 'pdf_download');
    onOpenChange(false);
    toast.info('PDF scaricato: trascinalo nella chat di WhatsApp che si è aperta, poi premi Invia.', { duration: 8000 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" /> Invia su WhatsApp
          </DialogTitle>
          <DialogDescription>
            Il messaggio parte dal tuo WhatsApp: controlla il testo e invia.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="wa-phone">Numero del cliente</Label>
            <Input id="wa-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="es. 333 123 4567" className="mt-1" data-testid="whatsapp-phone" />
            {phone && !number && <p className="text-xs text-red-600 mt-1">Numero non valido</p>}
            {number && <p className="text-xs text-[#4A5568] mt-1">Verrà usato +{number}</p>}
          </div>
          {usable.length > 0 && (
            <div>
              <Label>Modello</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {usable.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplateId(t.id)}
                    className={`text-xs px-2.5 py-1.5 rounded-sm border ${t.id === templateId ? 'bg-[#25D366]/10 border-[#25D366] text-[#0A0F1A]' : 'border-[#E2E8F0] text-[#4A5568]'}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="wa-text">Messaggio</Label>
            <Textarea id="wa-text" value={text} onChange={(e) => setText(e.target.value)} rows={6} className="mt-1 text-sm" data-testid="whatsapp-text" />
          </div>
          {quote?.id ? (
            <>
              <Button onClick={sendPdf} disabled={!pdfFile} className="w-full bg-[#25D366] hover:bg-[#1EBE5A] text-white" data-testid="whatsapp-send-pdf">
                <FilePdf size={18} weight="fill" className="mr-2" />
                {pdfFile ? 'Invia il PDF su WhatsApp' : 'Preparazione del PDF...'}
              </Button>
              <p className="text-xs text-[#4A5568] -mt-2">
                {canShareFile
                  ? 'Si apre il menu Condividi: scegli WhatsApp e poi il cliente. Il PDF arriva come allegato.'
                  : "Dal browser del PC il PDF viene scaricato e si apre la chat: trascina il file nella chat e invia. Dal telefono si apre direttamente il menu Condividi."}
              </p>
              <div className="border-t border-[#E2E8F0] pt-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <LinkIcon size={16} className="text-[#4A5568] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-[#0A0F1A]">Solo messaggio, con link al preventivo</p>
                      <p className="text-xs text-[#4A5568]">
                        {withLink && share ? `Link valido fino al ${fmtDate(share.expires_at)}. ` : ''}Il link mostra l'indirizzo infismart.it.
                      </p>
                    </div>
                  </div>
                  <Switch checked={withLink} onCheckedChange={setWithLink} aria-label="Aggiungi il link al preventivo" data-testid="whatsapp-link-switch" />
                </div>
                <Button variant="outline" onClick={openWhatsApp} className="w-full border-[#25D366] text-[#128C4B]" data-testid="whatsapp-open">
                  <WhatsappLogo size={18} weight="fill" className="mr-2 text-[#25D366]" /> Solo messaggio
                </Button>
              </div>
            </>
          ) : (
            <Button onClick={openWhatsApp} className="w-full bg-[#25D366] hover:bg-[#1EBE5A] text-white" data-testid="whatsapp-open">
              <WhatsappLogo size={18} weight="fill" className="mr-2" /> Apri WhatsApp
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
